import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Target, Heart, Globe, Users, Award, Lightbulb, TrendingUp, Shield } from 'lucide-react'
import { Logo } from './ui/Logo'
import { useI18n } from '../i18n'

const values = [
  { icon: Target, titleZh: '以客户成功为使命', titleEn: 'Customer Success First', descZh: '我们衡量自己的唯一标准是客户是否真正从AI中获得了价值', descEn: 'The only metric that matters is whether customers truly gain value from AI' },
  { icon: Lightbulb, titleZh: '技术创新驱动', titleEn: 'Innovation Driven', descZh: '持续投入大模型推理与知识检索的前沿研究，让产品始终保持领先', descEn: 'Continuous investment in frontier LLM reasoning & knowledge retrieval research' },
  { icon: Shield, titleZh: '安全可信', titleEn: 'Security & Trust', descZh: '企业数据隔离存储，支持私有化部署，安全合规是我们的底线', descEn: 'Data isolation, private deployment support — security compliance is our baseline' },
  { icon: Heart, titleZh: '简单易用', titleEn: 'Simplicity', descZh: '最强大的技术应该有最简单的体验，30分钟上线不是口号', descEn: 'The most powerful tech should have the simplest experience. 30-min deploy is not a slogan' },
]

const milestones = [
  { year: '2024', titleZh: '项目启动', titleEn: 'Project Launched', descZh: 'PrimeBook AI项目正式立项，聚焦企业知识管理与AI推理', descEn: 'PrimeBook AI officially launched, focusing on enterprise knowledge management & AI reasoning' },
  { year: '2024', titleZh: '核心引擎突破', titleEn: 'Core Engine Breakthrough', descZh: 'DeepSeek R1深度推理引擎接入，知识检索准确率达98%', descEn: 'DeepSeek R1 reasoning engine integrated, knowledge retrieval accuracy reached 98%' },
  { year: '2025', titleZh: '多行业落地', titleEn: 'Multi-Industry Deployment', descZh: '金融、餐饮、教育、制造、零售五大行业标杆客户上线', descEn: 'Benchmark customers live across finance, F&B, education, manufacturing & retail' },
  { year: '2025', titleZh: '生态开放', titleEn: 'Ecosystem Open', descZh: '开放API平台上线，支持企业系统无缝集成与私有化部署', descEn: 'Open API platform launched, supporting seamless integration & private deployment' },
]

const stats = [
  { numZh: '500+', numEn: '500+', labelZh: '企业客户', labelEn: 'Enterprise Clients' },
  { numZh: '1000万+', numEn: '10M+', labelZh: '月均对话量', labelEn: 'Monthly Conversations' },
  { numZh: '98%', numEn: '98%', labelZh: '知识检索准确率', labelEn: 'Retrieval Accuracy' },
  { numZh: '5', numEn: '5', labelZh: '覆盖行业', labelEn: 'Industries Covered' },
]

export default function AboutPage() {
  const navigate = useNavigate()
  const { lang, t } = useI18n()

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f3ff] via-[#faf8ff] to-[#f5f3ff]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-14">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-8 shrink-0">
            <Logo size={24} />
            <span className="text-[#1a1a1a]">PrimeBook</span>
            <span className="text-orange-500">AI</span>
          </Link>
          <button onClick={() => navigate('/')} className="px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
            <ArrowLeft className="h-3.5 w-3.5 inline mr-1.5" />
            {t('返回首页', 'Back')}
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center mb-20">
          <span className="inline-block px-3 py-1 text-xs font-medium text-violet-500 bg-violet-50 rounded-full mb-4">
            {t('关于我们', 'About Us')}
          </span>
          <h1 className="text-3xl md:text-5xl font-semibold text-[#1a1a1a] mb-4 tracking-tight">
            {t('让每个企业都能拥有自己的AI管家', 'Every Enterprise Deserves Its Own AI Butler')}
          </h1>
          <p className="text-base text-gray-400 max-w-2xl mx-auto leading-relaxed">
            {t(
              'PrimeBook AI 成立于2024年，是一家专注企业级AI应用的技术公司。我们坚信，AI不应只是大企业的特权，每个企业——无论规模大小——都值得拥有一个深度理解自身业务、7x24小时在线的专属AI管家。',
              'Founded in 2024, PrimeBook AI is a technology company dedicated to enterprise AI applications. We believe AI shouldn\'t be a luxury for big enterprises alone — every business, regardless of size, deserves a dedicated AI Butler that deeply understands their operations and is available 24/7.'
            )}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {stats.map((s, i) => (
            <div key={i} className="text-center p-6 rounded-2xl bg-white/60 border border-white/80">
              <div className="text-3xl font-bold text-[#1a1a1a] mb-1">{lang === 'zh' ? s.numZh : s.numEn}</div>
              <div className="text-xs text-gray-400">{lang === 'zh' ? s.labelZh : s.labelEn}</div>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="mb-20">
          <h2 className="text-2xl font-semibold text-[#1a1a1a] mb-8 tracking-tight text-center">
            {t('我们的价值观', 'Our Values')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {values.map((v, i) => (
              <div key={i} className="flex items-start gap-4 p-6 rounded-2xl bg-white/60 border border-white/80 hover:bg-white hover:shadow-md transition-all duration-300">
                <div className="h-10 w-10 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <v.icon className="h-5 w-5 text-violet-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#1a1a1a] mb-1">{lang === 'zh' ? v.titleZh : v.titleEn}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{lang === 'zh' ? v.descZh : v.descEn}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Milestones */}
        <div className="mb-20">
          <h2 className="text-2xl font-semibold text-[#1a1a1a] mb-8 tracking-tight text-center">
            {t('发展历程', 'Milestones')}
          </h2>
          <div className="relative">
            <div className="absolute left-[19px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-indigo-200 via-violet-200 to-orange-200" />
            <div className="space-y-6">
              {milestones.map((m, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="relative z-10 h-10 w-10 rounded-full bg-white border-2 border-violet-200 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-violet-500">{m.year.slice(2)}</span>
                  </div>
                  <div className="pt-1.5">
                    <h3 className="text-sm font-bold text-[#1a1a1a] mb-0.5">{lang === 'zh' ? m.titleZh : m.titleEn}</h3>
                    <p className="text-xs text-gray-400 leading-relaxed">{lang === 'zh' ? m.descZh : m.descEn}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vision */}
        <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-indigo-50/80 to-violet-50/80 border border-white/80">
          <Globe className="h-8 w-8 text-indigo-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#1a1a1a] mb-3">
            {t('我们的愿景', 'Our Vision')}
          </h2>
          <p className="text-sm text-gray-500 max-w-lg mx-auto leading-relaxed">
            {t(
              '成为全球企业最信赖的AI管家平台，让知识不再沉睡，让每个员工都拥有专家级的智能辅助。我们相信，当企业知识被AI充分激活，组织的创造力将被释放到前所未有的高度。',
              'To become the world\'s most trusted AI Butler platform, awakening dormant knowledge and giving every employee expert-level intelligent assistance. We believe that when enterprise knowledge is fully activated by AI, organizational creativity will reach unprecedented heights.'
            )}
          </p>
        </div>
      </div>
    </div>
  )
}
